import { useTranslation } from 'react-i18next'

/**
 * API Error response structure
 */
export interface ApiError {
  message: string
  status?: number
  code?: string
  details?: Record<string, unknown>
}

/**
 * Check if error is an API error with status
 */
export const isApiError = (
  error: unknown,
): error is ApiError & { status: number } =>
  error !== null &&
  typeof error === 'object' &&
  'status' in error &&
  typeof (error as ApiError).status === 'number'

export const getHttpStatus = (error: unknown): number | null => {
  if (typeof error !== 'object' || error === null) return null
  const shaped = error as {
    status?: number
    response?: { status?: number }
  }
  if (typeof shaped.status === 'number') return shaped.status
  if (typeof shaped.response?.status === 'number') return shaped.response.status
  return null
}

export const isRateLimitError = (error: unknown): boolean =>
  getHttpStatus(error) === 429
// test
/**
 * True when the browser couldn't read a response at all — real network outage,
 * CORS-blocked response, or DNS failure. Axios surfaces these as `ERR_NETWORK`
 * with no `response` object. Useful on screens like login where a CORS-masked
 * 429 is indistinguishable from a real outage and we want to gate retries.
 */
export const isNetworkError = (error: unknown): boolean => {
  if (typeof error !== 'object' || error === null) return false
  const shaped = error as { code?: string; response?: unknown }
  return shaped.code === 'ERR_NETWORK' && shaped.response === undefined
}

const readRetryAfterHeader = (error: unknown): string | null => {
  if (typeof error !== 'object' || error === null) return null
  const headers = (
    error as { response?: { headers?: Record<string, unknown> } }
  ).response?.headers
  const raw = headers?.['retry-after'] ?? headers?.['Retry-After']
  return raw === undefined || raw === null ? null : String(raw)
}

const parseRetryAfterValue = (raw: string): number | null => {
  const asNumber = Number(raw)
  if (Number.isFinite(asNumber) && asNumber > 0) return Math.ceil(asNumber)
  const asDate = Date.parse(raw)
  if (Number.isNaN(asDate)) return null
  const diff = Math.ceil((asDate - Date.now()) / 1000)
  return diff > 0 ? diff : null
}

/**
 * Read `retry_after` (seconds) out of a 429 response body. The platform's
 * rate-limit envelope carries it as `{ ..., retry_after: 873 }`, which survives
 * CORS even when the `Retry-After` header is stripped.
 */
const readRetryAfterBody = (error: unknown): number | null => {
  if (typeof error !== 'object' || error === null) return null
  const data = (error as { response?: { data?: unknown } }).response?.data
  if (typeof data !== 'object' || data === null) return null
  const value = (data as { retry_after?: unknown }).retry_after
  if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) {
    return null
  }
  return Math.ceil(value)
}

/**
 * Read how long to wait before retrying. Prefers the `Retry-After` header
 * (seconds or HTTP-date), then falls back to the `retry_after` field on the
 * response body — which CORS keeps even when the header is stripped. Returns
 * null when neither is present/parseable so the caller can use a sane default.
 */
export const getRetryAfterSeconds = (error: unknown): number | null => {
  const raw = readRetryAfterHeader(error)
  const fromHeader = raw === null ? null : parseRetryAfterValue(raw)
  return fromHeader ?? readRetryAfterBody(error)
}

function parseJsonOrNull(value: string): unknown {
  try {
    return JSON.parse(value)
  } catch {
    return null
  }
}

/**
 * Platform validation envelope: `{ detail: "Validation failed", errors: [{ message }] }`.
 * The top-level `detail` is generic, so prefer the specific per-field messages.
 */
function pickValidationErrors(data: unknown): string | null {
  if (typeof data !== 'object' || data === null) return null
  const errors = (data as { errors?: unknown }).errors
  if (!Array.isArray(errors)) return null
  const messages = errors
    .map((entry) =>
      entry && typeof entry === 'object' && 'message' in entry
        ? String((entry as { message: unknown }).message)
        : null,
    )
    .filter((message): message is string => Boolean(message))
  return messages.length > 0 ? messages.join('; ') : null
}

function pickDetail(data: unknown): string | null {
  if (typeof data !== 'object' || data === null) return null
  const validationErrors = pickValidationErrors(data)
  if (validationErrors) return validationErrors
  const detail = (data as { detail?: unknown }).detail
  if (typeof detail === 'string' && detail.length > 0) return detail
  if (Array.isArray(detail)) {
    const messages = detail
      .map((entry) =>
        entry && typeof entry === 'object' && 'msg' in entry
          ? String((entry as { msg: unknown }).msg)
          : null,
      )
      .filter((message): message is string => Boolean(message))
    if (messages.length > 0) return messages.join('; ')
  }
  return null
}

/**
 * Pull a human-readable `detail` string out of an API error response body
 * (e.g. AxiosError → response.data.detail). Handles both parsed objects and
 * JSON-stringified bodies, plus FastAPI's array-of-issues shape. Returns null
 * when the shape doesn't match — the caller falls back to a localized string.
 */
export const getApiErrorDetail = (error: unknown): string | null => {
  if (typeof error !== 'object' || error === null) return null
  const response = (error as { response?: { data?: unknown } }).response
  const data = response?.data
  const objectDetail = pickDetail(data)
  if (objectDetail) return objectDetail
  if (typeof data === 'string' && data.length > 0) {
    const parsed = parseJsonOrNull(data)
    const parsedDetail = pickDetail(parsed)
    if (parsedDetail) return parsedDetail
    return data
  }
  return null
}

/**
 * Read the machine-readable `code` out of the platform error envelope
 * (`response.data.code`, e.g. `NOT_NEW_ORDER`). Branch on this to pick copy —
 * never on the human-facing `message` string.
 */
export const getApiErrorCode = (error: unknown): string | null => {
  if (typeof error !== 'object' || error === null) return null
  const data = (error as { response?: { data?: unknown } }).response?.data
  const body = typeof data === 'string' ? parseJsonOrNull(data) : data
  if (typeof body !== 'object' || body === null) return null
  const code = (body as { code?: unknown }).code
  return typeof code === 'string' && code.length > 0 ? code : null
}

/**
 * Extract error message from various error types
 */
/**
 * A 403 the caller can do nothing about. The product also excludes its
 * device-approval 403, which the console never receives — it has no device
 * approval — so a plain 403 check is the same answer here.
 */
export const isPermissionDenied = (error: unknown): boolean => getHttpStatus(error) === 403

export const getErrorMessage = (error: unknown): string => {
  if (error instanceof Error) {
    return error.message
  }

  if (isApiError(error)) {
    return error.message || `HTTP ${error.status} Error`
  }

  if (typeof error === 'string') {
    return error
  }

  return 'An unknown error occurred'
}

/**
 * Log API error to console with structured format
 */
export const logApiError = (error: unknown, context?: string): void => {
  const timestamp = new Date().toISOString()
  const errorMessage = getErrorMessage(error)

  console.group(`🔥 API Error ${context ? `[${context}]` : ''} - ${timestamp}`)
  console.error('Message:', errorMessage)

  if (isApiError(error)) {
    console.error('Status:', error.status)
    if (error.code) console.error('Code:', error.code)
    if (error.details) console.error('Details:', error.details)
  }

  if (error instanceof Error && error.stack) {
    console.error('Stack:', error.stack)
  }

  console.error('Raw error:', error)
  console.groupEnd()
}

/**
 * Global API error handler for React Query
 */
export const createApiErrorHandler = () => {
  return {
    onError: (error: Error, query: { queryKey: readonly unknown[] }) => {
      const context = `Query: ${JSON.stringify(query.queryKey)}`
      logApiError(error, context)
    },
    onMutationError: (error: Error) => {
      logApiError(error, 'Mutation')
    },
  }
}

/**
 * Hook for handling API errors in components
 */
export const useApiErrorHandler = () => {
  const { t } = useTranslation()

  return {
    handleError: (error: unknown, context?: string) => {
      logApiError(error, context)

      // Return user-friendly message for UI
      if (isApiError(error)) {
        switch (error.status) {
          case 401:
            return t('errors.api.unauthorized')
          case 403:
            return t('errors.api.forbidden')
          case 404:
            return t('errors.api.notFound')
          case 500:
            return t('errors.api.serverError')
          case 503:
            return t('errors.api.serviceUnavailable')
          default:
            return t('errors.api.generic')
        }
      }

      return t('errors.api.networkError')
    },

    getErrorMessage: (error: unknown) => getErrorMessage(error),
  }
}
